/**
 * callerIdentity — resolve the authenticated caller's durable `personId` in the
 * browser, for the live document-upload transport (apply-document-uploads
 * Req 10.2, 10.3, 10.4).
 *
 * The shared document backend keys every `DocumentReference` by the owning
 * Contestant's durable `personId` (the `applicantId-competitionYear-index` GSI
 * the submit gate and the Document Center both read;
 * `application-form-service/ops/responseOps.ts → loadUploadedDocuments`). The
 * existing `getDocumentUploadUrl` / `createDocumentReference` /
 * `listDocumentReferences` GraphQL operations take that `applicantId` as an
 * argument, so the live client must resolve it — and it MUST resolve it from the
 * authenticated Cognito identity, never from a surface-supplied value, so a
 * caller can only ever read/write their OWN documents (Req 10.2, 10.3). An
 * unauthenticated caller cannot resolve an id at all, so the request is rejected
 * before it is issued (Req 10.1, 10.4).
 *
 * The derivation MUST match the server's owner-scoped rule exactly. The backend
 * derives `personId = personIdForCognitoSub(identity.sub)` via a deterministic
 * RFC-4122 v5 UUID of the Cognito `sub` within the fixed `PERSON_NAMESPACE`
 * (`infrastructure/lambda/person-service/personLogic.ts` +
 * `_shared/personIdContract.ts`, mirroring `boundFieldOps.selectDivision`). That
 * module is Node-only (`crypto` / `Buffer`) so it cannot be imported into the
 * browser bundle; this file reproduces the identical UUIDv5 algorithm on the
 * Web Crypto API (`crypto.subtle.digest('SHA-1', …)`), keeping the derived
 * `personId` byte-for-byte identical to the server's.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * The fixed namespace for the deterministic Person ids — IDENTICAL to
 * `personLogic.PERSON_NAMESPACE`. Changing it would break resolution against
 * every id the server has ever written, so it must stay in lockstep.
 */
const PERSON_NAMESPACE = '6f9619ff-8b86-d011-b42d-00cf4fc964ff';

/** Parse a canonical UUID string into its 16 raw bytes. */
function parseUuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '');
  if (hex.length !== 32 || /[^0-9a-fA-F]/.test(hex)) {
    throw new Error(`Invalid namespace UUID: ${uuid}`);
  }
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

/** Format 16 bytes as a canonical `8-4-4-4-12` UUID string. */
function bytesToUuid(bytes: Uint8Array): string {
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return (
    `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
    `${hex.slice(16, 20)}-${hex.slice(20, 32)}`
  );
}

/**
 * Deterministic RFC-4122 v5 UUID for `name` within `namespace` — the SHA-1 of
 * (namespace bytes ++ name bytes) truncated to 16 bytes with the version/variant
 * bits stamped. Reproduces `personLogic.uuidV5` on the Web Crypto API so the
 * result matches the server's derivation byte-for-byte.
 */
async function uuidV5(name: string, namespace: string = PERSON_NAMESPACE): Promise<string> {
  const nsBytes = parseUuidToBytes(namespace);
  const nameBytes = new TextEncoder().encode(name);
  const data = new Uint8Array(nsBytes.length + nameBytes.length);
  data.set(nsBytes, 0);
  data.set(nameBytes, nsBytes.length);

  const digest = new Uint8Array(await crypto.subtle.digest('SHA-1', data));
  const bytes = digest.slice(0, 16);
  bytes[6] = (bytes[6] & 0x0f) | 0x50; // version 5
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // RFC 4122 variant
  return bytesToUuid(bytes);
}

/** Deterministic `personId` for an authenticating human keyed by `cognitoSub`. */
export async function personIdForCognitoSub(cognitoSub: string): Promise<string> {
  return uuidV5(cognitoSub, PERSON_NAMESPACE);
}

/**
 * The authenticated caller's durable `personId`, derived from their Cognito
 * `sub`. Rejects when there is no authenticated session, so an unauthenticated
 * document request never proceeds (Req 10.1, 10.4).
 */
export async function resolveCallerPersonId(): Promise<string> {
  const session = await fetchAuthSession();
  const sub = session.tokens?.idToken?.payload?.sub;
  if (typeof sub !== 'string' || sub.length === 0) {
    throw new Error(
      'An authenticated caller is required to upload, list, or remove documents.',
    );
  }
  return personIdForCognitoSub(sub);
}
